// src/utils/error.utils.ts

/**
 * Safely extracts error message from unknown error type
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error !== null && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }

  return 'Unknown error';
}

/**
 * Creates a standardized error with context
 */
export function createContextualError(context: string, originalError: unknown): Error {
  const message = getErrorMessage(originalError);
  const contextualMessage = `${context}: ${message}`;

  const error = new Error(contextualMessage);

  // Preserve original error properties if available
  if (originalError instanceof Error) {
    const { stack } = originalError;
    if (stack !== undefined) {
      error.stack = stack;
    }
    Object.assign(error, originalError);
}

  return error;
}

/**
 * Wraps async operations with error context
 * Usage: return await withErrorContext('Operation failed', async () => { ... });
 */
export async function withErrorContext<T>(
  context: string,
  operation: () => Promise<T>
): Promise<T> {
  try {
    return await operation();
  } catch (error: unknown) {
    throw createContextualError(context, error);
  }
}

/**
 * Wraps synchronous operations with error context
 * Usage: return withSyncErrorContext('Operation failed', () => { ... });
 */
export function withSyncErrorContext<T>(
  context: string,
  operation: () => T
): T {
  try {
    return operation();
  } catch (error: unknown) {
    throw createContextualError(context, error);
  }
}

/**
 * Creates error with additional metadata
 */
export function createDetailedError(
  message: string,
  details?: Record<string, unknown>
): Error {
  const error = new Error(message);

  if (details !== undefined) {
    Object.assign(error, { details });
  }

  return error;
}

/**
 * Wraps async operations with detailed error context and metadata
 */
export async function withDetailedErrorContext<T>(
  context: string,
  operation: () => Promise<T>,
  metadata?: Record<string, unknown>
): Promise<T> {
  try {
    return await operation();
  } catch (error: unknown) {
    const contextualError = createContextualError(context, error);

    if (metadata !== undefined) {
      Object.assign(contextualError, { metadata });
    }

    throw contextualError;
  }
}
